import React from 'react';
import { useAdminTheme } from '../../contexts/AdminThemeContext';
import { AutomationWithUser } from '../../services/adminAutomationService';

interface AutomationThreadListProps {
  automations: AutomationWithUser[];
  selectedId: string | null;
  onSelect: (automation: AutomationWithUser) => void;
  loading: boolean;
}

const AutomationThreadList: React.FC<AutomationThreadListProps> = ({ 
  automations, 
  selectedId, 
  onSelect, 
  loading 
}) => {
  const { isDark } = useAdminTheme();

  if (loading) {
    return (
      <div className="flex flex-col space-y-0 p-0">
        {[1, 2, 3, 4, 5, 6, 7].map((i) => (
          <div key={i} className="animate-pulse flex gap-4 p-4 border-b border-slate-100 dark:border-slate-800">
            <div className={`w-10 h-10 rounded-full ${isDark ? 'bg-slate-800' : 'bg-slate-100'}`}></div>
            <div className="flex-1 space-y-3">
              <div className={`h-4 rounded w-1/4 ${isDark ? 'bg-slate-800' : 'bg-slate-100'}`}></div>
              <div className={`h-3 rounded w-3/4 ${isDark ? 'bg-slate-800' : 'bg-slate-100'}`}></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (automations.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 p-8 text-center">
        <div className={`text-[13px] font-medium ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>
          No automations found in this folder
        </div>
      </div>
    );
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return isDark 
          ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20' 
          : 'bg-emerald-50 text-emerald-700 border-emerald-100';
      case 'paused':
        return isDark 
          ? 'bg-amber-500/10 text-amber-500 border-amber-500/20' 
          : 'bg-amber-50 text-amber-700 border-amber-100';
      case 'draft':
        return isDark 
          ? 'bg-slate-700/50 text-slate-400 border-slate-700' 
          : 'bg-slate-100 text-slate-600 border-slate-200';
      default:
        return isDark 
          ? 'bg-slate-700/50 text-slate-400 border-slate-700' 
          : 'bg-slate-100 text-slate-600 border-slate-200';
    }
  };

  const formatDateShort = (timestamp: any) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    
    if (date.toDateString() === now.toDateString()) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
    }
    
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays < 7) {
      return date.toLocaleDateString([], { weekday: 'short' });
    }
    
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  return (
    <div className="flex flex-col divide-y divide-slate-100 dark:divide-slate-800">
      {automations.map((automation) => (
        <button
          key={automation.id}
          onClick={() => onSelect(automation)}
          className={`w-full flex items-start gap-4 px-5 py-4 transition-all text-left group relative ${
            selectedId === automation.id
              ? isDark
                ? 'bg-slate-800/80 shadow-sm'
                : 'bg-[#E8F0FE] shadow-sm z-10'
              : isDark
                ? 'bg-transparent hover:bg-slate-800/40'
                : 'bg-white hover:bg-slate-50'
          }`}
        >
          {/* Active selection indicator (v-bar) */}
          {selectedId === automation.id && (
            <div className={`absolute left-0 top-0 bottom-0 w-1 ${isDark ? 'bg-blue-500' : 'bg-[#0B57D0]'}`} />
          )}

          {/* Avatar / Icon */}
          <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center font-medium text-[15px] transition-colors ${
            selectedId === automation.id
              ? isDark ? 'bg-slate-700 text-white' : 'bg-white text-[#0B57D0]'
              : isDark ? 'bg-slate-800 text-slate-400' : 'bg-slate-100 text-slate-500'
          }`}>
            {automation.name.charAt(0).toUpperCase()}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-0.5">
              <h3 className={`text-[14px] font-semibold truncate ${
                isDark ? 'text-slate-100' : 'text-slate-900'
              } ${selectedId === automation.id ? 'font-bold' : ''}`}>
                {automation.name}
              </h3>
              <span className={`text-[11px] font-medium whitespace-nowrap ${
                selectedId === automation.id
                  ? isDark ? 'text-blue-400' : 'text-[#0B57D0]'
                  : isDark ? 'text-slate-500' : 'text-slate-400'
              }`}>
                {formatDateShort(automation.updatedAt)}
              </span>
            </div>
            
            <div className="flex items-center gap-2 mb-1.5">
               <span className={`px-1.5 py-0.5 border rounded text-[9px] font-bold uppercase tracking-tight ${getStatusBadge(automation.status)}`}>
                {automation.status}
              </span>
              <span className={`text-[11px] font-medium ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>
                {automation.userName || 'System'}
              </span>
            </div>

            <p className={`text-[12px] line-clamp-2 leading-relaxed ${
              selectedId === automation.id
                ? isDark ? 'text-slate-300' : 'text-slate-700'
                : isDark ? 'text-slate-500' : 'text-slate-500'
            }`}>
              {automation.description || 'No description provided for this automation.'}
            </p>
          </div>
        </button>
      ))}
    </div>
  );
};

export default AutomationThreadList;
